import React, { useState } from 'react';
import { Button, ConfirmationModal, LoadingSpinner, Message, SearchableSingleSelect, useFetch } from 'common.ui';
import { entraClient } from 'api/AdgangClients';
import { ResponseError } from 'api/adgang';
import { GenericValidationError } from 'api/adgang/models';

export interface ISecurityGroupMapping {
  securityGroupId?: string | null;
  displayName?: string | null;
}

export interface ISyncSummary {
  added?: number | null;
  skipped?: number | null;
  failed?: number | null;
}

interface IProps {
  // Id til tilgangen/rollen, brukes til å hente koblingen på nytt når den endres
  entityId: string;
  // F.eks. "tilgangen" eller "rollen" - brukes i teksten
  entityLabel: string;
  entityName?: string | null;
  // Hvem som regnes som innehavere, f.eks. "har tilgangen direkte eller via en rolle"
  holdersText: string;
  fetchMapping: () => Promise<ISecurityGroupMapping>;
  saveMapping: (securityGroupId: string | null) => Promise<ISecurityGroupMapping>;
  syncMembers: () => Promise<ISyncSummary>;
  onBack: () => void;
}

// Egen id for "ingen gruppe": SearchableSingleSelect tolker tom id som ingenting valgt og viser da et tomt felt
const NO_GROUP = 'ingen';

const statusText = (e: ResponseError) => `Uventet feil fra serveren (${e.response.status})`;

// Synkron variant for feil som ikke har lest svarteksten (f.eks. feil ved henting)
const errorText = (e: unknown) => {
  if (e instanceof ResponseError) return statusText(e);
  return e instanceof Error && e.message ? e.message : 'Ukjent feil';
};

// Klienten kaster ResponseError med en generisk engelsk melding. Serveren svarer 400 med
// GenericValidationError ({ messages: [], errors: { felt: tekst } }), så vi leser årsaken derfra.
// Svarteksten kan bare leses én gang.
const readErrorText = async (e: unknown): Promise<string> => {
  if (!(e instanceof ResponseError)) return errorText(e);
  try {
    const body = (await e.response.json()) as GenericValidationError | null;
    const texts = [...(body?.messages ?? []), ...Object.values(body?.errors ?? {})].filter(Boolean);
    if (texts.length > 0) return texts.join(' ');
  } catch {
    // Svaret var ikke JSON - bruk statuskoden i stedet
  }
  return statusText(e);
};

function SecurityGroupMappingForm({
  entityId,
  entityLabel,
  entityName,
  holdersText,
  fetchMapping,
  saveMapping,
  syncMembers,
  onBack
}: IProps) {
  const [groups, groupsLoading, groupsError] = useFetch(async () => entraClient.apiEntraSecurityGroupsGet(), [], false, []);
  const [mapping, mappingLoading, mappingError, refetchMapping] = useFetch<ISecurityGroupMapping | undefined>(
    fetchMapping,
    undefined,
    false,
    [entityId]
  );

  const [selectedId, setSelectedId] = useState<string>(NO_GROUP);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ type: 'confirmation' | 'error'; text: string } | undefined>(undefined);
  const [confirmSync, setConfirmSync] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<ISyncSummary | undefined>(undefined);
  const [syncError, setSyncError] = useState<string | undefined>(undefined);

  // Seed valget med lagret gruppe slik at "Lagre" uten endring beholder koblingen.
  // Gjøres under render når koblingen hentes på nytt, i stedet for i en effekt.
  const [previousMapping, setPreviousMapping] = useState(mapping);
  if (previousMapping !== mapping) {
    setPreviousMapping(mapping);
    setSelectedId(mapping?.securityGroupId || NO_GROUP);
  }

  const loading = groupsLoading || mappingLoading;
  const failed = groupsError || mappingError;

  const savedId = mapping?.securityGroupId ?? '';
  const savedGroup = (groups ?? []).find((g) => g.id === savedId);
  // Slettet gruppe: koblingen peker på en id som ikke finnes i Entra lenger
  const isDeleted = !!savedId && !savedGroup;
  const savedName = savedGroup?.displayName ?? mapping?.displayName ?? savedId;
  const deletedSelected = isDeleted && selectedId === savedId;

  // Valget øverst lar administrator fjerne koblingen (sender null ved lagring)
  const options = [
    { id: NO_GROUP, text: '(Ingen sikkerhetsgruppe)' },
    ...(isDeleted ? [{ id: savedId, text: `Slettet gruppe (${savedId})` }] : []),
    ...(groups ?? []).map((g) => ({ id: g.id ?? '', text: g.displayName ?? '' }))
  ];

  async function save() {
    setSaving(true);
    try {
      const result = await saveMapping(selectedId === NO_GROUP ? null : selectedId);
      setStatus({
        type: 'confirmation',
        text: result.securityGroupId
          ? `Lagret. Koblet til: ${result.displayName ?? result.securityGroupId}`
          : 'Lagret. Koblingen er fjernet.'
      });
      setSyncResult(undefined);
      setSyncError(undefined);
      await refetchMapping();
    } catch (e) {
      setStatus({ type: 'error', text: `Kunne ikke lagre: ${await readErrorText(e)}` });
    } finally {
      setSaving(false);
    }
  }

  async function sync() {
    setConfirmSync(false);
    setSyncing(true);
    setSyncResult(undefined);
    setSyncError(undefined);
    try {
      setSyncResult(await syncMembers());
    } catch (e) {
      setSyncError(`Kunne ikke synkronisere medlemmer: ${await readErrorText(e)}`);
    } finally {
      setSyncing(false);
    }
  }

  if (failed) {
    return (
      <div>
        <Message
          type='error'
          text={`Kunne ikke hente ${mappingError ? 'sikkerhetsgruppekoblingen' : 'sikkerhetsgruppene'} fra Entra: ${errorText(
            mappingError || groupsError
          )}. Prøv å laste siden på nytt.`}
        />
        <div style={{ marginTop: 24 }}>
          <Button styleType='light' type='button' text='Tilbake' onClick={onBack} />
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <p>
        Velg hvilken Entra-sikkerhetsgruppe brukere med {entityLabel}{' '}
        {entityName && <strong>{entityName}</strong>} skal
        synkroniseres til. Velg «(Ingen sikkerhetsgruppe)» for å fjerne koblingen.
      </p>
      <p>
        Nåværende kobling:{' '}
        <strong>{savedId ? (isDeleted ? `Slettet gruppe (${savedId})` : savedName) : 'Ingen sikkerhetsgruppe'}</strong>
      </p>
      {isDeleted && (
        <Message
          type='warning'
          text={`Gruppen er slettet i Entra eller finnes ikke lenger (${savedId}). Velg en ny gruppe, eller fjern koblingen.`}
        />
      )}
      <SearchableSingleSelect
        key={savedId || NO_GROUP}
        id='entraSecurityGroup'
        label='Endre sikkerhetsgruppe'
        options={options}
        selectedId={selectedId}
        onChange={(val: string) => {
          setSelectedId(val || NO_GROUP);
          setStatus(undefined);
          setSyncResult(undefined);
          setSyncError(undefined);
        }}
      />
      {status && (
        <div style={{ marginTop: 16 }}>
          <Message type={status.type} text={status.text} />
        </div>
      )}
      <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
        <Button type='button' text='Lagre' onClick={save} loading={saving} disabled={saving || syncing || deletedSelected} />
        <Button styleType='light' type='button' text='Tilbake' onClick={onBack} />
      </div>

      {savedId && !isDeleted && (
        <div style={{ marginTop: 32 }}>
          <p>
            Synkroniserer alle interne brukere (ansatte) som {holdersText} i dag inn i gruppen. Eksterne
            brukere, for eksempel Statsforvalteren, legges ikke til, og det samme gjelder interne brukere som
            mangler Entra-bruker (telles som hoppet over). Ingen fjernes fra gruppen. Brukere som allerede er
            medlemmer teller som lagt til, uten at noe endres. Synkroniseringen gjelder lagret kobling.
          </p>
          <Button
            type='button'
            text='Synkroniser medlemmer'
            styleType='light'
            onClick={() => setConfirmSync(true)}
            loading={syncing}
            disabled={syncing || saving}
          />
        </div>
      )}
      {syncResult && (
        <div role='status'>
          <p>
            Synkronisering ferdig. Lagt til (inkludert de som allerede var medlemmer): {syncResult.added ?? 0}.
            Hoppet over: {syncResult.skipped ?? 0}. Feilet: {syncResult.failed ?? 0}.
          </p>
          {(syncResult.failed ?? 0) > 0 && (
            <Message
              type='warning'
              text={`Antall brukere som ikke kunne legges til i gruppen: ${syncResult.failed}. Prøv å synkronisere på nytt, eller kontakt support hvis feilen vedvarer.`}
            />
          )}
        </div>
      )}
      {syncError && <Message type='error' text={syncError} />}

      <ConfirmationModal
        header='Synkroniser medlemmer'
        show={confirmSync}
        onCancel={() => setConfirmSync(false)}
        onAccept={sync}
        yesText='Synkroniser'
        noText='Avbryt'
      >
        Er du sikker på at du vil legge til alle interne brukere som {holdersText} i
        sikkerhetsgruppen <strong>{savedName}</strong>? Ingen fjernes fra gruppen.
      </ConfirmationModal>
    </div>
  );
}

export default SecurityGroupMappingForm;
